import { useState, useCallback } from 'react';
import {
  SPIN_SAVE_MSG,
  SPIN_DISCARD_MSG,
  SPIN_LOCKED_CLOSE,
  SPIN_LOCKED_DELETE,
} from './useSpinner';
import ErrorHelpers from '~/app/errorHelpers.js';

/**
 * useReturnClose
 *
 * Owns the confirm-dialog state and all save/close/delete actions that were
 * previously class methods on FormViewer:
 *
 *   getReturnMetadata()       — build the metadata array from formList
 *   saveReturn()              — save without closing
 *   closeAndSaveReturn()      — save then close (ReturnSaveAndClose)
 *   closeReturn()             — close without save (read-only style)
 *   deleteReturn()            — delete the return
 *   closeWithDelay(discard)   — start spinner before a delayed close
 *   closeLockedReturn()       — spinner + close for a locked return
 *   showConfirmClose()        — decide whether to auto-close or show dialog
 *   hideConfirmClose()        — dismiss the close-confirm dialog
 *   showConfirmDeleteReturn() — open the delete-confirm dialog
 *   hideConfirmDeleteReturn() — dismiss the delete-confirm dialog
 *
 * State returned for render:
 *   confirmReturnClose   — boolean
 *   confirmDeleteReturn  — boolean
 *
 * Callers must populate returnCloseCallbacksRef.current in
 * componentDidMount / componentDidUpdate so the hook can reach back into
 * the class component for the pieces it doesn't own:
 *
 *   onHasFLST()                   → boolean
 *   onHandleRequireFilingStatusDlg()
 *   onCloseActiveReturn()
 *   onStartSpin(msg)
 *   onShowErrorSnackbar(msg)
 *   getFormList()                 → formList array  (called at call-time, not captured)
 *   getSeason()                   → season string
 *   getIsWizard()                 → boolean
 *   getIsReadOnly()               → boolean
 *   getReturnInitialized()        → boolean
 *   getSilentlySaveReturnOnClose()→ string ('0' | '1')
 */
const useReturnClose = ({ calcAPIRef, returnCloseCallbacksRef }) => {
  const [confirmReturnClose, setConfirmReturnClose] = useState(false);
  const [confirmDeleteReturn, setConfirmDeleteReturn] = useState(false);

  // ─── helpers ──────────────────────────────────────────────────────────────

  const cb = () => returnCloseCallbacksRef.current;

  const getReturnMetadata = useCallback(() => {
    const formList = cb().getFormList() ?? [];
    const season = cb().getSeason();
    const metaData = [];

    for (let i = 1; i < formList.length; i++) {
      const form = formList[i];
      if (!form) continue;

      if (form.desc && form.desc.trim() === 'FEDERAL') {
        let fedRefundOrBalDue = 0;
        if (form.rfnd && form.rfnd !== '') {
          fedRefundOrBalDue = parseInt(form.rfnd.substring(1).replace(/,/g, ''), 0);
        } else if (form.bdue && form.bdue !== '') {
          fedRefundOrBalDue = parseInt(form.bdue.replace(/\$/g, '-').replace(/,/g, ''), 0);
        }
        metaData.push({ stateName: 'FEDERAL', refundOrBalDue: fedRefundOrBalDue, season });
      } else if (form.var && form.var.length <= 2) {
        // exclude interview mode forms
        const stateName = form.desc;
        let stateRefundOrBalDue = 0;
        if (form.rfnd && form.rfnd !== '') {
          stateRefundOrBalDue = parseInt(form.rfnd.substring(1).replace(/,/g, ''), 0);
        } else if (form.bdue && form.bdue !== '') {
          stateRefundOrBalDue = parseInt(form.bdue.replace(/\$/g, '-').replace(/,/g, ''), 0);
        }
        metaData.push({ stateName: stateName.trim(), refundOrBalDue: stateRefundOrBalDue, season });
      }
    }
    return metaData;
  }, [returnCloseCallbacksRef]);

  // ─── close / save / delete actions ────────────────────────────────────────

  // Starts the spinner with the appropriate message; the actual close is
  // triggered by the calc server response handled in useSocketMessage.
  const closeWithDelay = useCallback(
    discard => {
      cb().onStartSpin(discard ? SPIN_DISCARD_MSG : SPIN_SAVE_MSG);
    },
    [returnCloseCallbacksRef],
  );

  const closeLockedReturn = useCallback(() => {
    cb().onStartSpin(SPIN_LOCKED_CLOSE);
    cb().onCloseActiveReturn();
  }, [returnCloseCallbacksRef]);

  const closeAndSaveReturn = useCallback(async () => {
    if (!cb().onHasFLST()) {
      cb().onHandleRequireFilingStatusDlg();
    } else {
      try {
        cb().onStartSpin(SPIN_SAVE_MSG);
        calcAPIRef.current?.ReturnSaveAndClose(getReturnMetadata(), cb().getIsWizard());
      } catch (error) {
        ErrorHelpers.handleError('Error saving return', error);
      }
    }
  }, [calcAPIRef, getReturnMetadata, returnCloseCallbacksRef]);

  const saveReturn = useCallback(async () => {
    if (!cb().onHasFLST()) {
      cb().onHandleRequireFilingStatusDlg();
    } else {
      try {
        cb().onStartSpin(SPIN_SAVE_MSG);
        calcAPIRef.current?.ReturnSave(
          getReturnMetadata(),
          cb().getIsReadOnly(),
          cb().getIsWizard(),
        );
      } catch (error) {
        cb().onShowErrorSnackbar('Error saving return');
        ErrorHelpers.handleError('Error saving return', error);
      }
    }
  }, [calcAPIRef, getReturnMetadata, returnCloseCallbacksRef]);

  const closeReturn = useCallback(async () => {
    try {
      cb().onStartSpin(SPIN_LOCKED_CLOSE);
      calcAPIRef.current?.ReturnClose(cb().getIsReadOnly());
    } catch (error) {
      ErrorHelpers.handleError('Error closing return', error);
    }
  }, [calcAPIRef, returnCloseCallbacksRef]);

  const deleteReturn = useCallback(async () => {
    try {
      cb().onStartSpin(SPIN_LOCKED_DELETE);
      calcAPIRef.current?.ReturnDelete();
    } catch (error) {
      ErrorHelpers.handleError('Error deleting return', error);
    }
  }, [calcAPIRef, returnCloseCallbacksRef]);

  // ─── confirm dialog controls ───────────────────────────────────────────────

  const showConfirmClose = useCallback(() => {
    if (!cb().getReturnInitialized() || cb().getIsReadOnly()) {
      closeReturn();
      return;
    }
    if (cb().getSilentlySaveReturnOnClose() === '1') {
      closeAndSaveReturn();
    } else {
      setConfirmReturnClose(true);
    }
  }, [closeReturn, closeAndSaveReturn, returnCloseCallbacksRef]);

  const hideConfirmClose = useCallback(() => setConfirmReturnClose(false), []);
  const showConfirmDeleteReturn = useCallback(() => setConfirmDeleteReturn(true), []);
  const hideConfirmDeleteReturn = useCallback(() => setConfirmDeleteReturn(false), []);

  return {
    // state
    confirmReturnClose,
    confirmDeleteReturn,
    setConfirmReturnClose,
    setConfirmDeleteReturn,
    // actions
    getReturnMetadata,
    closeWithDelay,
    closeLockedReturn,
    closeAndSaveReturn,
    saveReturn,
    closeReturn,
    deleteReturn,
    showConfirmClose,
    hideConfirmClose,
    showConfirmDeleteReturn,
    hideConfirmDeleteReturn,
  };
};

export default useReturnClose;
